import { useEffect, useRef } from 'react';

/* Ambiente do console: campo de pontos que acende perto do ponteiro e as duas
   variáveis de mouse (--mx e --my) que dão a profundidade 3D ao resto da interface.
   A mira é o próprio cursor do sistema (desenhado em CSS), por isso não tem atraso. */

export function Ambient({ hud = false }: { hud?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = canvas.current;
    const ctx = cv?.getContext('2d') ?? null;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = matchMedia('(pointer: fine)').matches;
    const root = document.documentElement;
    const dpr = Math.min(devicePixelRatio || 1, 2);
    let w = 0, h = 0, t = 0, last = 0;
    let px = innerWidth / 2, py = innerHeight / 2;
    let rafField = 0, rafVars = 0;

    const size = () => {
      if (!cv || !ctx) return;
      w = cv.clientWidth; h = cv.clientHeight;
      cv.width = Math.max(1, Math.round(w * dpr));
      cv.height = Math.max(1, Math.round(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    addEventListener('resize', size);

    /* As variáveis mexem em muitos elementos: atualiza no máximo uma vez por quadro. */
    const writeVars = () => {
      rafVars = 0;
      root.style.setProperty('--mx', ((px / innerWidth - 0.5) * 2).toFixed(3));
      root.style.setProperty('--my', ((py / innerHeight - 0.5) * 2).toFixed(3));
    };
    const onMove = (e: PointerEvent) => {
      px = e.clientX; py = e.clientY;
      if (!rafVars) rafVars = requestAnimationFrame(writeVars);
    };
    addEventListener('pointermove', onMove, { passive: true });

    /* Pulso nascendo do ponto tocado, em qualquer botão do console. */
    const onClick = (e: MouseEvent) => {
      const btn = (e.target as Element | null)?.closest?.('.btn, .chip') as HTMLElement | null;
      if (!btn || btn.hasAttribute('disabled') || reduce) return;
      const r = btn.getBoundingClientRect();
      const s = Math.max(r.width, r.height);
      const sp = document.createElement('span');
      sp.className = 'ripple';
      sp.style.width = `${s}px`;
      sp.style.height = `${s}px`;
      sp.style.left = `${e.clientX - r.left - s / 2}px`;
      sp.style.top = `${e.clientY - r.top - s / 2}px`;
      btn.appendChild(sp);
      setTimeout(() => sp.remove(), 560);
    };
    addEventListener('click', onClick);

    const STEP = 36;
    const field = (now: number) => {
      if (!ctx) return;
      rafField = requestAnimationFrame(field);
      if (now - last < 33) return;
      last = now;
      t += 0.011;
      ctx.clearRect(0, 0, w, h);
      const ox = Math.sin(t) * 5, oy = Math.cos(t * 0.8) * 5;
      for (let x = STEP / 2; x < w; x += STEP) {
        for (let y = STEP / 2; y < h; y += STEP) {
          const dx = x - px, dy = y - py;
          const d = Math.sqrt(dx * dx + dy * dy);
          const k = d < 240 ? 1 - d / 240 : 0;
          const s = 1 + k * 2;
          ctx.fillStyle = `rgba(63,210,255,${(0.045 + k * 0.45).toFixed(3)})`;
          ctx.fillRect(x + ox * (1 - k) - s / 2, y + oy * (1 - k) - s / 2, s, s);
        }
      }
      if (fine) {
        ctx.strokeStyle = 'rgba(63,210,255,0.09)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(px, py, 118 + Math.sin(t * 2) * 9, 0, Math.PI * 2);
        ctx.stroke();
      }
    };
    if (!reduce && ctx) rafField = requestAnimationFrame(field);

    return () => {
      cancelAnimationFrame(rafField);
      cancelAnimationFrame(rafVars);
      removeEventListener('resize', size);
      removeEventListener('pointermove', onMove);
      removeEventListener('click', onClick);
    };
  }, []);

  if (hud) return null;
  return (
    <>
      <canvas ref={canvas} className="amb-field" aria-hidden="true" />
      <div className="amb-aura" aria-hidden="true" />
      <div className="amb-scan" aria-hidden="true" />
      <div className="amb-vig" aria-hidden="true" />
      <span className="amb-corner tl" aria-hidden="true" />
      <span className="amb-corner tr" aria-hidden="true" />
      <span className="amb-corner bl" aria-hidden="true" />
      <span className="amb-corner br" aria-hidden="true" />
    </>
  );
}
